/**
 * @file index.tsx
 *
 * @fileoverview An long selectable box to choose things from.
 */
import { cva, VariantProps } from "class-variance-authority";
import { ReactNode } from "react";
import CheckDoubleIcon from "@jq-icons/CheckDouble";
import FlexItems from "../../../02-atoms/06-layout/03-flex";
import FlexItem from "../../../02-atoms/06-layout/03-flex/02-flex-item";
import Spacing from "../../../02-atoms/06-layout/07-spacing";
import Box from "../../../02-atoms/07-box";
import Text from "../../../02-atoms/13-text/03-text";
import styles from "./longBoxSelectable.module.scss";

const longSelectabeBox = cva([styles.longSelectableBox], {
  variants: {
    selected: { true: styles.longSelectableBoxSelected },
    disabled: { true: styles.longBoxSelectableDisabled },
    color: {
      default: styles.longSelectableBoxdefault,
      primary: styles.longSelectableBoxprimary,
      secondary: styles.longSelectableBoxsecondary,
      success: styles.longSelectableBoxsuccess,
      danger: styles.longSelectableBoxdanger,
      warning: styles.longSelectableBoxwarning,
    },
  },
  defaultVariants: { selected: false, color: "primary", disabled: false },
});
const wrapButton = cva([styles.wrapButton], {
  variants: {
    disabled: {
      true: styles.wrapButtonDisabled,
    },
  },
  defaultVariants: {
    disabled: false,
  },
});
const checkCircle = cva([styles.checkCircle], {
  variants: {
    selected: { true: styles.checkCircleSelected },
    color: {
      default: styles.checkCircledefault,
      primary: styles.checkCircleprimary,
      secondary: styles.checkCirclesecondary,
      success: styles.checkCirclesuccess,
      danger: styles.checkCircledanger,
      warning: styles.checkCirclewarning,
    },
  },
  defaultVariants: { selected: false, color: "primary" },
});
const middleIcon = cva([styles.middleIcon], {
  variants: {
    selected: { true: styles.middleIconSelected },
    color: {
      default: styles.middleIcondefault,
      primary: styles.middleIconprimary,
      secondary: styles.middleIconsecondary,
      success: styles.middleIconsuccess,
      danger: styles.middleIcondanger,
      warning: styles.middleIconwarning,
    },
  },
  defaultVariants: { color: "primary" },
});

export interface LongBoxSelectableProps
  extends VariantProps<typeof longSelectabeBox> {
  buttonId?: string;
  /**
   * For a controlled version use this to provide the value if its selected or not.
   */
  selected?: boolean;
  /**
   * The title for the box.
   */
  title: string;
  /**
   * The icon to render inside the box.
   *
   * @default null
   */
  icon?: ReactNode;
  /**
   * The title for the icon inside the box.
   *
   * @default null
   */
  iconTitle?: string;
  /**
   * The style for the box.
   *
   * @default primary
   */
  /**
   * Handler to be called when clicking.
   *
   * @default null
   */
  onClick?: () => void;
  /**
   * Text to render inside the box.
   *
   * @default null
   */
  children: ReactNode;
  /**
   * If the box is disabled.
   */
  disabled?: boolean;
}

/**
 * A long selectable box can be used for multi selection steps or pages like inside an onboarding wizard. Used
 * for the same functionality as checkboxes but in a more stylistic way.
 */
export default function LongBoxSelectable({
  buttonId,
  title,
  selected = false,
  onClick,
  icon,
  iconTitle,
  color = "primary",
  children,
  disabled = false,
  ...props
}: LongBoxSelectableProps) {
  return (
    <button
      className={wrapButton({ disabled })}
      {...(buttonId ? { id: buttonId } : null)}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      <Box
        className={longSelectabeBox({ color, selected, disabled })}
        data-testid="selectable-box"
      >
        <FlexItems flexWrap="nowrap" flexDirection={["column", "row"]}>
          {icon && (
            <FlexItem marginRight={[0, 8]} marginBottom={[6, 0]}>
              <span className={middleIcon({ color, selected })}>{icon}</span>
            </FlexItem>
          )}
          <FlexItem>
            <Text tag="div" marginTop={0} fontWeight="bold">
              {title}
            </Text>
            <Spacing size="small" />
            <div className={styles.longBoxContent}>{children}</div>
          </FlexItem>
          <FlexItem alignSelf="start" justifySelf="end">
            <div className={checkCircle({ color, selected })}>
              {selected && (
                <span
                  data-test-id="long-box-selected"
                  className={styles.checkIcon}
                >
                  <CheckDoubleIcon title={iconTitle} />
                </span>
              )}
            </div>
          </FlexItem>
        </FlexItems>
      </Box>
    </button>
  );
}
export { LongBoxSelectable };
